/**
 * WCWN Mobile Drawer (Gallery Aesthetic)
 * Handles interactions: Open/Close, Root <-> Detail Views, Category Rail
 */

(function () {
    'use strict';

    const SELECTORS = {
        drawer: '[data-drawer]',
        overlay: '[data-drawer-overlay]',
        toggle: '[data-mobile-menu-toggle]',
        close: '[data-drawer-close]',
        viewRoot: '[data-drawer-view="root"]',
        viewDetail: '[data-view-id]',
        trigger: '[data-view-trigger]', // For main menu links
        categoryTrigger: '[data-category-trigger]', // For rail items
        back: '[data-view-back]',
        body: 'body'
    };

    const CLASSES = {
        isOpen: 'is-open',
        isVisible: 'is-visible',
        isMounted: 'is-mounted', // Gates will-change; matches cart / SAM.STYLE / filter
        isActive: 'is-active',
        noScroll: 'no-scroll',
        viewDetailActive: 'view-detail-active' // Applied to main drawer for styling root view parallax
    };

    /* Drawer motion lives in CSS custom properties; never re-hardcode it here. */
    function drawerTiming() {
        if (window.WCWYDrawerController && window.WCWYDrawerController.timing) {
            return window.WCWYDrawerController.timing();
        }
        return { open: 360, close: 260, openSettle: 380, closeFallback: 340 };
    }

    /* Start the slide on a frame the browser has already painted. */
    function drawerAfterPaint(fn) {
        if (window.WCWYDrawerController && window.WCWYDrawerController.afterPaint) {
            return window.WCWYDrawerController.afterPaint(fn);
        }
        return requestAnimationFrame(() => requestAnimationFrame(fn));
    }

    let drawer, overlay, closeButtons, backButtons;
    let lastFocusedElement = null;
    let resetTimer = null;
    let closeTransitionHandler = null;
    let pendingOpenFrame = null;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function setInert(element, shouldBeInert) {
        if (!element) return;
        element.inert = shouldBeInert;
        if (shouldBeInert) {
            element.setAttribute('inert', '');
        } else {
            element.removeAttribute('inert');
        }
    }

    function setViewState(view, isActive) {
        if (!view) return;
        view.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        setInert(view, !isActive);
    }

    function init() {
        const mobileQuery = window.matchMedia('(max-width: 798px)');

        // Teardown first to avoid duplicate bindings
        teardown();

        if (mobileQuery.matches) {
            bind(mobileQuery);
            if (drawer) drawer.classList.add('is-ready');
        }

        // Listen for Resize
        mobileQuery.addEventListener('change', (e) => {
            if (e.matches) {
                bind(mobileQuery);
                if (drawer) drawer.classList.add('is-ready');
            } else {
                teardown();
                closeDrawer(); // Ensure it closes if resizing to desktop
            }
        });
    }

    function bind(mq) {
        // Global Toggles (outside drawer)
        const toggles = document.querySelectorAll(SELECTORS.toggle);
        toggles.forEach(btn => btn.addEventListener('click', openDrawer));

        // Drawer Elements
        drawer = document.querySelector(SELECTORS.drawer);
        overlay = document.querySelector(SELECTORS.overlay);

        if (!drawer || !overlay) return;

        if (window.WCWYDrawerController) {
            window.WCWYDrawerController.register('mobile-menu', {
                drawer: drawer,
                overlay: overlay,
                manageFocus: false,
                isOpen: () => drawer.classList.contains(CLASSES.isOpen),
                close: closeDrawer
            });
        }

        // Bind Close Buttons (Scoped)
        closeButtons = drawer.querySelectorAll(SELECTORS.close);
        closeButtons.forEach(btn => btn.addEventListener('click', closeDrawer));

        // Bind Overlay Click
        overlay.addEventListener('click', closeDrawer);

        // Bind Main Menu Triggers (Scoped)
        const triggers = drawer.querySelectorAll(SELECTORS.trigger);
        triggers.forEach(btn => {
            btn.addEventListener('click', (e) => {
                const targetId = btn.getAttribute('data-view-trigger');
                openDetailView(targetId);
            });
        });

        // Bind Category Rail Triggers (Scoped)
        const catTriggers = drawer.querySelectorAll(SELECTORS.categoryTrigger);
        const allRailItems = drawer.querySelectorAll('.kith-drawer__rail-item');

        catTriggers.forEach(btn => {
            btn.addEventListener('click', (e) => {
                const targetId = btn.getAttribute('data-category-trigger');

                // Clear ALL rail items (including WCWN) then activate clicked
                allRailItems.forEach(b => b.classList.remove(CLASSES.isActive));
                btn.classList.add(CLASSES.isActive);

                openDetailView(targetId);
            });
        });

        // Bind ALL WCWN "root" rail buttons (one per view)
        const wcwyBtns = drawer.querySelectorAll('[data-rail-target="root"]');
        wcwyBtns.forEach(btn => {
            btn.addEventListener('click', (e) => {
                resetToRoot();
            });
        });

        // Bind detail-view rail items ([data-category]) — switch between detail views
        const detailRailItems = drawer.querySelectorAll('[data-category]');
        detailRailItems.forEach(btn => {
            btn.addEventListener('click', (e) => {
                const targetId = btn.getAttribute('data-category');
                openDetailView(targetId);
            });
        });

        // Bind Back Buttons (Scoped)
        backButtons = drawer.querySelectorAll(SELECTORS.back);
        backButtons.forEach(btn => btn.addEventListener('click', closeDetailView));

        // Keyboard dismissal and focus containment
        document.addEventListener('keydown', handleKeydown);
    }

    function teardown() {
        // Remove Global Listeners
        const toggles = document.querySelectorAll(SELECTORS.toggle);
        toggles.forEach(btn => btn.removeEventListener('click', openDrawer));

        document.removeEventListener('keydown', handleKeydown);

        // We don't necessarily need to remove internal scoped listeners if the elements persist, 
        // but keeping it clean is good. 
        // For simplicity/performance in this pattern, we just stop listening to new triggers.
    }

    function setExpandedState(isExpanded) {
        document.querySelectorAll(SELECTORS.toggle).forEach(btn => {
            btn.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
            btn.setAttribute('aria-label', isExpanded ? 'Close menu' : 'Open menu');
        });

        if (drawer) {
            drawer.setAttribute('aria-hidden', isExpanded ? 'false' : 'true');
            setInert(drawer, !isExpanded);
        }
        if (overlay) overlay.setAttribute('aria-hidden', isExpanded ? 'false' : 'true');
    }

    function getActiveView() {
        if (!drawer) return null;
        return drawer.querySelector('.kith-drawer__view--detail.is-active')
            || drawer.querySelector(SELECTORS.viewRoot);
    }

    function handleKeydown(e) {
        if (!drawer || !drawer.classList.contains(CLASSES.isOpen)) return;

        if (e.key === 'Escape') {
            closeDrawer();
            return;
        }

        if (e.key !== 'Tab') return;

        const activeView = getActiveView();
        if (!activeView) return;

        const focusable = Array.from(activeView.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ));
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    }

    function openDrawer(e) {
        if (e) e.preventDefault();
        if (!drawer || !overlay) return;
        if (drawer.classList.contains(CLASSES.isOpen)) return;

        lastFocusedElement = e && e.currentTarget ? e.currentTarget : document.activeElement;

        if (closeTransitionHandler) {
            drawer.removeEventListener('transitionend', closeTransitionHandler);
            closeTransitionHandler = null;
        }
        window.clearTimeout(resetTimer);
        if (pendingOpenFrame) window.cancelAnimationFrame(pendingOpenFrame);
        resetToRoot();

        drawer.classList.add('is-ready');
        drawer.classList.add(CLASSES.isMounted);
        overlay.classList.add(CLASSES.isMounted);
        overlay.classList.add(CLASSES.isVisible);
        setExpandedState(true);
        if (window.WCWYDrawerController) {
            window.WCWYDrawerController.open('mobile-menu');
        } else {
            document.body.classList.add(CLASSES.noScroll);
        }

        // Pay the mount layout cost now, while nothing is animating, then let the
        // browser paint a clean frame before the slide starts. Doing this work in
        // the frame that starts the transform dropped its first frames, which on
        // a decelerate curve is where most of the travel happens.
        drawer.offsetHeight;

        pendingOpenFrame = drawerAfterPaint(() => {
            drawer.classList.add(CLASSES.isOpen);
            pendingOpenFrame = null;
            const closeButton = getActiveView() && getActiveView().querySelector(SELECTORS.close);
            if (closeButton) closeButton.focus({ preventScroll: true });
        });
    }

    function closeDrawer() {
        if (!drawer || !overlay) return;
        if (!drawer.classList.contains(CLASSES.isOpen) && !overlay.classList.contains(CLASSES.isVisible)) return;

        drawer.classList.remove(CLASSES.isOpen);
        overlay.classList.remove(CLASSES.isVisible);
        setExpandedState(false);
        if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
            lastFocusedElement.focus({ preventScroll: true });
        }
        lastFocusedElement = null;
        if (window.WCWYDrawerController) {
            window.WCWYDrawerController.close('mobile-menu');
        } else {
            document.body.classList.remove(CLASSES.noScroll);
        }

        if (closeTransitionHandler) {
            drawer.removeEventListener('transitionend', closeTransitionHandler);
            closeTransitionHandler = null;
        }

        if (prefersReducedMotion.matches) {
            resetToRoot();
            return;
        }

        // Reset views after the drawer slide-out finishes.
        window.clearTimeout(resetTimer);
        closeTransitionHandler = (event) => {
            if (event.target !== drawer || event.propertyName !== 'transform') return;
            if (closeTransitionHandler) {
                drawer.removeEventListener('transitionend', closeTransitionHandler);
                closeTransitionHandler = null;
            }
            window.clearTimeout(resetTimer);
            resetToRoot();
        };
        drawer.addEventListener('transitionend', closeTransitionHandler);

        resetTimer = window.setTimeout(() => {
            if (closeTransitionHandler) {
                drawer.removeEventListener('transitionend', closeTransitionHandler);
                closeTransitionHandler = null;
            }
            resetToRoot();
        }, drawerTiming().closeFallback);
    }

    /** Goal B: Helper to reset drawer to root view with WCWN active */
    function resetToRoot() {
        if (!drawer) return;

        // Release the compositor layer once the slide-out has finished. openDrawer
        // calls resetToRoot() before it re-adds is-mounted, so this is safe there.
        if (!drawer.classList.contains(CLASSES.isOpen)) {
            drawer.classList.remove(CLASSES.isMounted);
            if (overlay) overlay.classList.remove(CLASSES.isMounted);
        }

        const focusedDetail = document.activeElement
            && document.activeElement.closest
            && document.activeElement.closest(SELECTORS.viewDetail);

        // Close all detail views
        const activeViews = drawer.querySelectorAll(SELECTORS.viewDetail);
        activeViews.forEach(view => {
            view.classList.remove(CLASSES.isActive);
            setViewState(view, false);
        });
        const rootView = drawer.querySelector(SELECTORS.viewRoot);
        setViewState(rootView, true);
        drawer.classList.remove(CLASSES.viewDetailActive);

        // Set WCWN as active in ALL rails (root + detail rails)
        const allRailItems = drawer.querySelectorAll('.kith-drawer__rail-item');
        allRailItems.forEach(item => {
            item.classList.remove(CLASSES.isActive);
            // Check if this is a WCWN root trigger
            if (item.hasAttribute('data-rail-target') && item.getAttribute('data-rail-target') === 'root') {
                item.classList.add(CLASSES.isActive);
            }
        });

        if (focusedDetail && drawer.classList.contains(CLASSES.isOpen) && rootView) {
            const rootFocusTarget = rootView.querySelector(SELECTORS.close + ', a[href], button');
            if (rootFocusTarget) rootFocusTarget.focus({ preventScroll: true });
        }
    }

    function openDetailView(id) {
        if (!drawer) return;

        const targetView = drawer.querySelector(`[data-view-id="${id}"]`);
        const focusedRoot = document.activeElement
            && document.activeElement.closest
            && document.activeElement.closest(SELECTORS.viewRoot);

        // 1. Activate the View
        if (targetView) {
            // Close other detail views first to avoid stacking
            const activeViews = drawer.querySelectorAll(SELECTORS.viewDetail);
            activeViews.forEach(view => {
                const isTarget = view === targetView;
                view.classList.toggle(CLASSES.isActive, isTarget);
                setViewState(view, isTarget);
            });

            targetView.classList.add(CLASSES.isActive);
            const rootView = drawer.querySelector(SELECTORS.viewRoot);
            setViewState(rootView, false);
            drawer.classList.add(CLASSES.viewDetailActive);

            if (focusedRoot) {
                const detailFocusTarget = targetView.querySelector(SELECTORS.back + ', ' + SELECTORS.close + ', a[href], button');
                if (detailFocusTarget) detailFocusTarget.focus({ preventScroll: true });
            }
        }

        // 2. Sync Rail State across all views (root rail + all detail rails)
        const allRailItems = drawer.querySelectorAll('.kith-drawer__rail-item');
        allRailItems.forEach(item => {
            item.classList.remove(CLASSES.isActive); // clear all first

            // Match by either data-category-trigger (root rail) or data-category (detail rails)
            if (item.getAttribute('data-category-trigger') === id ||
                item.getAttribute('data-category') === id) {
                item.classList.add(CLASSES.isActive);
            }
        });
    }

    function closeDetailView(e) {
        if (e) e.preventDefault();
        // The "Back" button effectively returns to Root (WCWN)
        resetToRoot();
    }

    // Initialize on load
    document.addEventListener('DOMContentLoaded', init);

    // Re-init on Shopify Section Load (for Theme Editor)
    document.addEventListener('shopify:section:load', init);

})();
